/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Lock,
  AlertTriangle,
  RotateCw,
  Clock,
  Sparkles,
  Users,
  Compass,
} from 'lucide-react';
import {
  TablePlan,
  Decoration,
  ReservationPlan,
  FormeTable,
  StatutVisuelTable,
  InfoOccupationTable,
} from './types.ts';
import {
  calculerAimantation,
  evaluerStatutTable,
  aimanterSurGrille,
  TAILLE_GRILLE,
  heureDe,
} from './logiquePure.ts';

interface VueSVGPlanProps {
  tables: TablePlan[];
  decorations: Decoration[];
  reservations: ReservationPlan[];
  heureAffichee: string;
  tableSelectionneeId?: string;
  tablesSelectionneesMulti?: string[];
  reservationMiseEnEvidenceId?: string;
  lectureSeule?: boolean;
  onSelectionnerTable: (tableId: string, estMulti?: boolean) => void;
  onDeplacerTable: (tableId: string, posX: number, posY: number) => void;
  onPivoterTable: (tableId: string, rotation: number) => void;
  onOuvrirMenuTable: (tableId: string, point?: { x: number; y: number }) => void;
  theme?: 'clair' | 'sombre';
}

export const VueSVGPlan: React.FC<VueSVGPlanProps> = ({
  tables,
  decorations,
  reservations,
  heureAffichee,
  tableSelectionneeId,
  tablesSelectionneesMulti = [],
  reservationMiseEnEvidenceId,
  lectureSeule = false,
  onSelectionnerTable,
  onDeplacerTable,
  onPivoterTable,
  onOuvrirMenuTable,
  theme = 'clair',
}) => {
  const conteneurRef = useRef<HTMLDivElement>(null);

  // État de la vue : Pan & Zoom
  const [zoom, setZoom] = useState<number>(1);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 20, y: 20 });
  const [estEnGlissementFond, setEstEnGlissementFond] = useState<boolean>(false);
  const dernierPointerFond = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Pincement tactile (pinch-to-zoom)
  const pointeursActifs = useRef<Map<number, { x: number; y: number }>>(new Map());
  const distancePincementInitiale = useRef<number | null>(null);
  const zoomInitialPincement = useRef<number>(1);

  // État local de déplacement d'une table (pour fluidité 60fps sans laguer le parent)
  const [tableEnDeplacement, setTableEnDeplacement] = useState<{
    id: string;
    origX: number;
    origY: number;
    curX: number;
    curY: number;
    debutClientX: number;
    debutClientY: number;
    aimantee: boolean;
    tableRefId?: string;
  } | null>(null);

  // Poignée de rotation en glissement
  const [tableEnRotation, setTableEnRotation] = useState<{
    id: string;
    centreX: number;
    centreY: number;
    angleInitial: number;
  } | null>(null);

  // Minuteur appui long tactile (500ms) pour ouvrir menu d'actions
  const timerAppuiLong = useRef<number | null>(null);

  // Annonce aria-live pour accessibilité
  const [annonceAccessibilite, setAnnonceAccessibilite] = useState<string>('');

  // Tables associées à la réservation mise en évidence (depuis la colonne des réservations)
  const tablesMisesEnEvidence = useMemo(() => {
    if (!reservationMiseEnEvidenceId) return new Set<string>();
    const res = reservations.find((r) => r.id === reservationMiseEnEvidenceId);
    return new Set<string>(res ? res.tableIds : []);
  }, [reservationMiseEnEvidenceId, reservations]);

  // Réinitialiser / recentrer le plan
  const recentrerPlan = useCallback(() => {
    setZoom(1);
    setPan({ x: 40, y: 30 });
    setAnnonceAccessibilite('Plan recentré à l’échelle 100%');
  }, []);

  // Gestion du zoom molette
  const gererMolette = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const facteur = e.deltaY < 0 ? 1.1 : 0.9;
    setZoom((z) => Math.min(2.5, Math.max(0.5, Number((z * facteur).toFixed(2)))));
  }, []);

  // Gestion du glissement de fond (Pan)
  const demarrerPanFond = (e: React.PointerEvent) => {
    // Si on a cliqué sur le fond SVG directement
    if ((e.target as Element).tagName === 'svg' || (e.target as Element).id === 'fond-plan') {
      pointeursActifs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointeursActifs.current.size === 1) {
        setEstEnGlissementFond(true);
        dernierPointerFond.current = { x: e.clientX, y: e.clientY };
        (e.target as Element).setPointerCapture(e.pointerId);
      }
    }
  };

  const deplacerPanFond = (e: React.PointerEvent) => {
    if (pointeursActifs.current.has(e.pointerId)) {
      pointeursActifs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    // Gestion du pinch à deux doigts
    if (pointeursActifs.current.size === 2) {
      const points = Array.from(pointeursActifs.current.values());
      const dist = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);

      if (distancePincementInitiale.current === null) {
        distancePincementInitiale.current = dist;
        zoomInitialPincement.current = zoom;
      } else {
        const ratio = dist / distancePincementInitiale.current;
        const nouvZoom = Math.min(2.5, Math.max(0.5, Number((zoomInitialPincement.current * ratio).toFixed(2))));
        setZoom(nouvZoom);
      }
      return;
    }

    // Gestion du pan simple à un doigt / souris
    if (estEnGlissementFond) {
      const dx = e.clientX - dernierPointerFond.current.x;
      const dy = e.clientY - dernierPointerFond.current.y;
      dernierPointerFond.current = { x: e.clientX, y: e.clientY };
      setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
    }

    // Gestion du drag d'une table
    if (tableEnDeplacement && !lectureSeule) {
      if (timerAppuiLong.current) {
        window.clearTimeout(timerAppuiLong.current);
        timerAppuiLong.current = null;
      }

      const dxSvg = (e.clientX - tableEnDeplacement.debutClientX) / zoom;
      const dySvg = (e.clientY - tableEnDeplacement.debutClientY) / zoom;

      const bruteX = tableEnDeplacement.origX + dxSvg;
      const bruteY = tableEnDeplacement.origY + dySvg;

      // Table déplacée
      const tableActuelle = tables.find((t) => t.id === tableEnDeplacement.id);
      if (tableActuelle) {
        const autres = tables.filter((t) => t.id !== tableEnDeplacement.id);
        const aimant = calculerAimantation(
          { x: bruteX, y: bruteY },
          tableActuelle.largeur,
          tableActuelle.hauteur,
          autres
        );

        setTableEnDeplacement((prev) =>
          prev
            ? {
                ...prev,
                curX: aimant.x,
                curY: aimant.y,
                aimantee: aimant.aimanteeAuBord,
                tableRefId: aimant.tableReferenceId,
              }
            : null
        );
      }
    }

    // Gestion de la rotation par poignée
    if (tableEnRotation && !lectureSeule) {
      const rect = conteneurRef.current?.getBoundingClientRect();
      if (rect) {
        // Coordonnées du curseur dans le référentiel SVG
        const mouseSvgX = (e.clientX - rect.left - pan.x) / zoom;
        const mouseSvgY = (e.clientY - rect.top - pan.y) / zoom;

        const rad = Math.atan2(mouseSvgY - tableEnRotation.centreY, mouseSvgX - tableEnRotation.centreX);
        let deg = Math.round((rad * (180 / Math.PI)) / 15) * 15; // Pas de 15°
        if (deg < 0) deg += 360;

        onPivoterTable(tableEnRotation.id, deg);
      }
    }
  };

  const arreterPointer = (e: React.PointerEvent) => {
    pointeursActifs.current.delete(e.pointerId);
    if (pointeursActifs.current.size < 2) {
      distancePincementInitiale.current = null;
    }

    if (timerAppuiLong.current) {
      window.clearTimeout(timerAppuiLong.current);
      timerAppuiLong.current = null;
    }

    if (estEnGlissementFond) {
      setEstEnGlissementFond(false);
    }

    // Terminer le drag d'une table
    if (tableEnDeplacement) {
      const { id, curX, curY, origX, origY } = tableEnDeplacement;
      if (Math.abs(curX - origX) > 2 || Math.abs(curY - origY) > 2) {
        onDeplacerTable(id, curX, curY);
        const t = tables.find((x) => x.id === id);
        setAnnonceAccessibilite(`Table ${t?.nom || id} déplacée en X=${curX}, Y=${curY}`);
      }
      setTableEnDeplacement(null);
    }

    if (tableEnRotation) {
      setTableEnRotation(null);
    }
  };

  // Démarrer le drag d'une table
  const demarrerDragTable = (e: React.PointerEvent, table: TablePlan) => {
    e.stopPropagation();

    // Sélection
    const estMulti = e.shiftKey;
    onSelectionnerTable(table.id, estMulti);

    // Si clic droit ou non modifiable
    if (e.button === 2) {
      onOuvrirMenuTable(table.id, { x: e.clientX, y: e.clientY });
      return;
    }

    if (lectureSeule) return;

    // Déclencher le timer d'appui long pour mobile (500ms)
    timerAppuiLong.current = window.setTimeout(() => {
      onOuvrirMenuTable(table.id, { x: e.clientX, y: e.clientY });
    }, 500);

    setTableEnDeplacement({
      id: table.id,
      origX: table.posX,
      origY: table.posY,
      curX: table.posX,
      curY: table.posY,
      debutClientX: e.clientX,
      debutClientY: e.clientY,
      aimantee: false,
    });
  };

  // Navigation au clavier pour la table sélectionnée (Accessibilité WCAG)
  const gererClavier = (e: React.KeyboardEvent) => {
    if (!tableSelectionneeId || lectureSeule) return;
    const table = tables.find((t) => t.id === tableSelectionneeId);
    if (!table) return;

    let deltaX = 0;
    let deltaY = 0;

    switch (e.key) {
      case 'ArrowLeft':
        deltaX = -TAILLE_GRILLE;
        break;
      case 'ArrowRight':
        deltaX = TAILLE_GRILLE;
        break;
      case 'ArrowUp':
        deltaY = -TAILLE_GRILLE;
        break;
      case 'ArrowDown':
        deltaY = TAILLE_GRILLE;
        break;
      case 'r':
      case 'R':
        onPivoterTable(table.id, (table.rotation + 15) % 360);
        setAnnonceAccessibilite(`Table ${table.nom} pivotée à ${(table.rotation + 15) % 360}°`);
        e.preventDefault();
        return;
      case 'Enter':
        onOuvrirMenuTable(table.id);
        e.preventDefault();
        return;
      case 'Escape':
        // Échap : annule d'abord un glissement ou une rotation en cours (la table revient où elle était), sinon désélectionne.
        if (tableEnDeplacement || tableEnRotation) { setTableEnDeplacement(null); setTableEnRotation(null); setAnnonceAccessibilite('Déplacement annulé'); e.preventDefault(); return; }
        onSelectionnerTable('');
        e.preventDefault();
        return;
      default:
        return;
    }

    if (deltaX !== 0 || deltaY !== 0) {
      e.preventDefault();
      const nouvX = Math.max(0, table.posX + deltaX);
      const nouvY = Math.max(0, table.posY + deltaY);
      onDeplacerTable(table.id, nouvX, nouvY);
      setAnnonceAccessibilite(`Table ${table.nom} déplacée en position X=${nouvX}, Y=${nouvY}`);
    }
  };

  // Poignées STABLES : elles lisent les dernières valeurs par une ref, de sorte que RenduTableSVG (memo) ne se redessine que si SA table change.
  // Pendant un glissement, seule la table déplacée est redessinée (80 tables : le reste du plan ne bouge pas).
  const courant = useRef({ demarrerDragTable, onOuvrirMenuTable, onSelectionnerTable });
  courant.current = { demarrerDragTable, onOuvrirMenuTable, onSelectionnerTable };
  const surPointerDown = useCallback((e: React.PointerEvent, t: TablePlan) => courant.current.demarrerDragTable(e, t), []);
  const surContexte = useCallback((e: React.MouseEvent, t: TablePlan) => { e.preventDefault(); courant.current.onOuvrirMenuTable(t.id, { x: e.clientX, y: e.clientY }); }, []);
  const surRotation = useCallback((e: React.PointerEvent, t: TablePlan) => {
    e.stopPropagation();
    setTableEnRotation({ id: t.id, centreX: t.posX + t.largeur / 2, centreY: t.posY + t.hauteur / 2, angleInitial: t.rotation });
  }, []);
  const surActiver = useCallback((t: TablePlan) => courant.current.onSelectionnerTable(t.id, false), []);
  const statutsParTable = useMemo(() => new Map(tables.map((t) => [t.id, evaluerStatutTable(t, reservations, heureAffichee)])), [tables, reservations, heureAffichee]);

  return (
    <div
      ref={conteneurRef}
      tabIndex={0}
      onKeyDown={gererClavier}
      onWheel={gererMolette}
      onPointerDown={demarrerPanFond}
      onPointerMove={deplacerPanFond}
      onPointerUp={arreterPointer}
      onPointerCancel={arreterPointer}
      aria-label="Plan de salle interactif à l'échelle. Utilisez les flèches pour déplacer une table sélectionnée, R pour pivoter, Entrée pour le menu d'actions."
      className="relative flex-1 w-full h-full min-h-[420px] bg-slate-100 dark:bg-slate-950 overflow-hidden outline-none cursor-grab active:cursor-grabbing select-none"
    >
      {/* Zone aria-live pour les lecteurs d'écran */}
      <div className="sr-only" aria-live="polite">
        {annonceAccessibilite}
      </div>

      {/* Boutons flottants de contrôle du plan (Zoom, Recentrer) */}
      <div
        className="absolute top-3 right-3 z-20 flex flex-col items-center gap-1.5 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xs p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm"
        role="toolbar"
        aria-label="Contrôles d'affichage du plan"
      >
        <button
          type="button"
          onClick={() => setZoom((z) => Math.min(2.5, Number((z + 0.15).toFixed(2))))}
          aria-label="Zoomer"
          className="p-2 rounded text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 min-h-[40px] min-w-[40px] flex items-center justify-center cursor-pointer transition-colors"
          title="Zoom +"
        >
          <ZoomIn className="w-4 h-4" />
        </button>

        <span className="text-[10px] font-mono font-bold text-slate-500 dark:text-slate-400 select-none px-1">
          {Math.round(zoom * 100)}%
        </span>

        <button
          type="button"
          onClick={() => setZoom((z) => Math.max(0.5, Number((z - 0.15).toFixed(2))))}
          aria-label="Dézoomer"
          className="p-2 rounded text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 min-h-[40px] min-w-[40px] flex items-center justify-center cursor-pointer transition-colors"
          title="Zoom -"
        >
          <ZoomOut className="w-4 h-4" />
        </button>

        <div className="w-full h-px bg-slate-200 dark:bg-slate-800 my-0.5" />

        <button
          type="button"
          onClick={recentrerPlan}
          aria-label="Recentrer le plan"
          className="p-2 rounded text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 min-h-[40px] min-w-[40px] flex items-center justify-center cursor-pointer transition-colors"
          title="Recentrer le plan"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>

      {/* Boussole / Orientation discrète */}
      <div className="absolute bottom-3 left-3 z-10 hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded bg-white/80 dark:bg-slate-900/80 backdrop-blur-xs text-[11px] text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-800 shadow-2xs pointer-events-none">
        <Compass className="w-3.5 h-3.5 text-slate-400" />
        <span>1 case = {TAILLE_GRILLE} cm • Aimantation active</span>
      </div>

      {/* Canevas SVG natif */}
      <svg
        id="fond-plan"
        className="w-full h-full block"
        xmlns="http://www.w3.org/2000/svg"
        onContextMenu={(e) => e.preventDefault()}
      >
        <defs>
          {/* Grille discrète de 20px */}
          <pattern
            id="grille-20"
            width={TAILLE_GRILLE}
            height={TAILLE_GRILLE}
            patternUnits="userSpaceOnUse"
          >
            <circle
              cx={TAILLE_GRILLE / 2}
              cy={TAILLE_GRILLE / 2}
              r={0.8}
              fill={'var(--border-color)'}
            />
          </pattern>

          {/* Motif hachures pour tables bloquées */}
          <pattern
            id="hachures-bloquee"
            width="10"
            height="10"
            patternTransform="rotate(45 0 0)"
            patternUnits="userSpaceOnUse"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="10"
              stroke={'var(--pds-danger-fg)'}
              strokeWidth="2.5"
              strokeOpacity="0.4"
            />
          </pattern>

          {/* Motif hachures pour tables en conflit */}
          <pattern
            id="hachures-conflit"
            width="8"
            height="8"
            patternTransform="rotate(-45 0 0)"
            patternUnits="userSpaceOnUse"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="8"
              stroke="var(--pds-danger-fg)"
              strokeWidth="2.5"
              strokeOpacity="0.6"
            />
          </pattern>

          {/* Ombre portée pour les tables */}
          <filter id="ombre-table" x="-10%" y="-10%" width="125%" height="125%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.12" />
          </filter>

          {/* Lueur pour table sélectionnée */}
          <filter id="lueur-selection" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="var(--pds-info-fg)" floodOpacity="0.6" />
          </filter>
        </defs>

        {/* Groupe principal transformé par Pan & Zoom */}
        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {/* Arrière-plan quadrillé */}
          <rect
            x={-1000}
            y={-1000}
            width={4000}
            height={4000}
            fill="url(#grille-20)"
            className="pointer-events-auto"
          />

          {/* Décorations fixes de la zone (murs, bar, escaliers, plantes, portes, piliers) */}
          {decorations.map((deco) => (
            <RenduDecoration key={deco.id} decoration={deco} theme={theme} />
          ))}

          {/* Rendu des tables du plan */}
          {tables.map((table) => {
            const enDeplacement = tableEnDeplacement?.id === table.id;
            const xAffiche = enDeplacement ? tableEnDeplacement.curX : table.posX;
            const yAffiche = enDeplacement ? tableEnDeplacement.curY : table.posY;

            const estSelectionnee =
              tableSelectionneeId === table.id || tablesSelectionneesMulti.includes(table.id);
            const estMiseEnEvidence = tablesMisesEnEvidence.has(table.id);

            const statutInfo = statutsParTable.get(table.id) as InfoOccupationTable;

            return (
              <RenduTableSVG
                key={table.id}
                table={enDeplacement ? { ...table, posX: xAffiche, posY: yAffiche } : table}
                statutInfo={statutInfo}
                estSelectionnee={estSelectionnee}
                estMiseEnEvidence={estMiseEnEvidence}
                enDeplacement={enDeplacement}
                lectureSeule={lectureSeule}
                theme={theme}
                onPointerDown={surPointerDown}
                onContextMenu={surContexte}
                onDemarrerRotation={surRotation}
                onActiver={surActiver}
              />
            );
          })}

          {/* Repère d'aimantation visuel (ligne magnétique quand table collée) */}
          {tableEnDeplacement?.aimantee && (
            <LigneRepereAimantation
              tableDeplacee={tables.find((t) => t.id === tableEnDeplacement.id)!}
              curX={tableEnDeplacement.curX}
              curY={tableEnDeplacement.curY}
            />
          )}
        </g>
      </svg>
    </div>
  );
};

/**
 * Rendu vectoriel d'une décoration fixe (non réservable).
 */
const RenduDecoration: React.FC<{ decoration: Decoration; theme: 'clair' | 'sombre' }> = React.memo(
  ({ decoration, theme }) => {
    const { posX, posY, largeur, hauteur, rotation, type } = decoration;
    const estSombre = theme === 'sombre';

    return (
      <g
        transform={`translate(${posX}, ${posY}) rotate(${rotation} ${largeur / 2} ${hauteur / 2})`}
        className="pointer-events-none select-none"
      >
        {type === 'mur' && (
          <rect
            width={largeur}
            height={hauteur}
            rx={2}
            fill={'var(--text-muted)'}
            stroke={'var(--text-secondary)'}
            strokeWidth="1.5"
          />
        )}

        {type === 'bar' && (
          <g>
            <rect
              width={largeur}
              height={hauteur}
              rx={6}
              fill={'var(--border-soft)'}
              stroke={'var(--border-color)'}
              strokeWidth="2"
            />
            <rect
              x={4}
              y={4}
              width={largeur - 8}
              height={hauteur - 8}
              rx={4}
              fill="none"
              stroke={'var(--pds-trait)'}
              strokeWidth="1"
              strokeDasharray="4 3"
            />
            <text
              x={largeur / 2}
              y={hauteur / 2 + 4}
              textAnchor="middle"
              fontSize="11"
              fontWeight="bold"
              fill={'var(--text-muted)'}
              letterSpacing="1.5"
            >
              BAR COMPTOIR
            </text>
          </g>
        )}

        {type === 'escalier' && (
          <g>
            <rect
              width={largeur}
              height={hauteur}
              fill={'var(--bg-soft)'}
              stroke={'var(--border-color)'}
              strokeWidth="1.5"
            />
            {Array.from({ length: 5 }).map((_, i) => (
              <line
                key={i}
                x1={(largeur / 5) * (i + 1)}
                y1={0}
                x2={(largeur / 5) * (i + 1)}
                y2={hauteur}
                stroke={'var(--pds-trait)'}
                strokeWidth="1"
              />
            ))}
            <text
              x={largeur / 2}
              y={hauteur / 2 + 3}
              textAnchor="middle"
              fontSize="9"
              fontWeight="bold"
              fill={'var(--text-muted)'}
            >
              ÉSCALIER
            </text>
          </g>
        )}

        {type === 'porte' && (
          <g>
            <line
              x1={0}
              y1={hauteur / 2}
              x2={largeur}
              y2={hauteur / 2}
              stroke={'var(--pds-trait)'}
              strokeWidth="2"
              strokeDasharray="3 3"
            />
            <path
              d={`M 0,${hauteur / 2} A ${largeur},${largeur} 0 0,1 ${largeur},0`}
              fill="none"
              stroke={'var(--text-muted)'}
              strokeWidth="1.5"
            />
            <text
              x={largeur / 2}
              y={-4}
              textAnchor="middle"
              fontSize="9"
              fill={'var(--text-muted)'}
            >
              ACCÈS
            </text>
          </g>
        )}

        {type === 'plante' && (
          <g>
            <circle
              cx={largeur / 2}
              cy={hauteur / 2}
              r={largeur / 2 - 2}
              fill={'var(--pds-ok-bg)'}
              stroke={'var(--pds-ok-fg)'}
              strokeWidth="2"
            />
            <path
              d={`M ${largeur / 2},${hauteur / 2 - 6} Q ${largeur / 2 + 5},${hauteur / 2} ${largeur / 2},${hauteur / 2 + 6} Q ${largeur / 2 - 5},${hauteur / 2} ${largeur / 2},${hauteur / 2 - 6}`}
              fill={'var(--pds-ok-fg)'}
            />
          </g>
        )}

        {type === 'pilier' && (
          <g>
            <rect
              width={largeur}
              height={hauteur}
              fill={'var(--text-muted)'}
              stroke={'var(--text-secondary)'}
              strokeWidth="1.5"
            />
            <line
              x1={0}
              y1={0}
              x2={largeur}
              y2={hauteur}
              stroke={'var(--pds-trait)'}
              strokeWidth="1"
            />
            <line
              x1={largeur}
              y1={0}
              x2={0}
              y2={hauteur}
              stroke={'var(--pds-trait)'}
              strokeWidth="1"
            />
          </g>
        )}
      </g>
    );
  }
);

/**
 * Rendu d'une table avec sièges dessinés autour, libellés, forme géométrique et état multi-indicateur.
 */
interface RenduTableSVGProps {
  table: TablePlan;
  statutInfo: InfoOccupationTable;
  estSelectionnee: boolean;
  estMiseEnEvidence: boolean;
  enDeplacement: boolean;
  lectureSeule: boolean;
  theme: 'clair' | 'sombre';
  onPointerDown: (e: React.PointerEvent, table: TablePlan) => void;
  onContextMenu: (e: React.MouseEvent, table: TablePlan) => void;
  onDemarrerRotation: (e: React.PointerEvent, table: TablePlan) => void;
  onActiver: (table: TablePlan) => void;
}

const RenduTableSVG: React.FC<RenduTableSVGProps> = React.memo(
  ({
    table,
    statutInfo,
    estSelectionnee,
    estMiseEnEvidence,
    enDeplacement,
    lectureSeule,
    theme,
    onPointerDown,
    onContextMenu,
    onDemarrerRotation,
    onActiver,
  }) => {
    const { posX, posY, largeur, hauteur, rotation, forme, nom, capaciteMax, bloquee, groupeId } =
      table;
    const estSombre = theme === 'sombre';
    const statut = statutInfo.statut;
    // Réservation écrite sur la table (utile en service : qui est là, ou qui arrive, sans ouvrir la table). Seulement si la table est assez haute pour la lire.
    const reservationAffichee = statutInfo.reservationEnCours ?? statutInfo.prochaineReservation;
    const maxCaracteres = Math.max(6, Math.floor(largeur / 7));
    const texteReservation = reservationAffichee && hauteur >= 70 ? `${heureDe(reservationAffichee.debut)} ${reservationAffichee.nom.length > maxCaracteres ? `${reservationAffichee.nom.slice(0, maxCaracteres - 1)}…` : reservationAffichee.nom}` : null;

    // Définition visuelle selon statut : COULEURS + FORMES + ICÔNES
    // Ne jamais reposer sur la couleur seule (WCAG 2.2 AA)
    const styleStatut = useMemo(() => {
      switch (statut) {
        case 'bloquee':
          return {
            fillTable: 'url(#hachures-bloquee)',
            strokeTable: 'var(--pds-danger-fg)',
            badgeBg: 'var(--pds-danger-fg)',
            badgeTexte: '#ffffff',
            libelleCourt: 'BLOQUÉE',
            formeSymbole: 'cadenas',
          };
        case 'conflit':
          return {
            fillTable: 'url(#hachures-conflit)',
            strokeTable: 'var(--pds-danger-fg)',
            badgeBg: 'var(--pds-danger-fg)',
            badgeTexte: '#ffffff',
            libelleCourt: 'CONFLIT',
            formeSymbole: 'danger',
          };
        case 'a_liberer':
          return {
            fillTable: 'var(--pds-warn-bg)',
            strokeTable: 'var(--pds-warn-fg)',
            badgeBg: 'var(--pds-warn-fg)',
            badgeTexte: '#ffffff',
            libelleCourt: 'À LIBÉRER',
            formeSymbole: 'triangle',
            pulsant: true,
          };
        case 'en_cours':
          return {
            fillTable: 'var(--pds-info-bg)',
            strokeTable: 'var(--pds-info-fg)',
            badgeBg: 'var(--pds-info-fg)',
            badgeTexte: '#ffffff',
            libelleCourt: 'EN COURS',
            formeSymbole: 'carre',
          };
        case 'arrivee':
          return {
            fillTable: 'var(--pds-ok-bg)',
            strokeTable: 'var(--pds-ok-fg)',
            badgeBg: 'var(--pds-ok-fg)',
            badgeTexte: '#ffffff',
            libelleCourt: 'INSTALLÉE',
            formeSymbole: 'carre_plein',
          };
        case 'reservee':
          return {
            fillTable: estSombre ? '#1e1b4b' : '#f5f3ff',
            strokeTable: '#7c3aed',
            badgeBg: '#7c3aed',
            badgeTexte: '#ffffff',
            libelleCourt: 'RÉSERVÉE',
            formeSymbole: 'losange',
          };
        case 'a_debarrasser':
          return {
            fillTable: 'var(--pds-warn-bg)',
            strokeTable: 'var(--pds-warn-fg)',
            badgeBg: 'var(--pds-warn-fg)',
            badgeTexte: '#ffffff',
            libelleCourt: 'À DÉBARRASSER',
            formeSymbole: 'assiette',
          };
        case 'libre':
        default:
          return {
            fillTable: 'var(--bg-card)',
            strokeTable: 'var(--border-color)',
            badgeBg: 'var(--bg-soft)',
            badgeTexte: 'var(--text-secondary)',
            libelleCourt: 'LIBRE',
            formeSymbole: 'cercle_vide',
          };
      }
    }, [statut, estSombre]);

    // Calcul des petits sièges autour de la table
    const sieges = useMemo(() => {
      return calculerSiegesAutour(largeur, hauteur, forme, capaciteMax);
    }, [largeur, hauteur, forme, capaciteMax]);

    return (
      <g
        transform={`translate(${posX}, ${posY}) rotate(${rotation} ${largeur / 2} ${hauteur / 2})`}
        className={`cursor-pointer ${enDeplacement ? 'opacity-85' : ''}`}
        data-table-id={table.id}
        tabIndex={0}
        role="button"
        aria-pressed={estSelectionnee}
        aria-label={`Table ${nom}, ${capaciteMax} place${capaciteMax > 1 ? 's' : ''}, ${statut.replace(/_/g, ' ')}${bloquee ? ', bloquée' : ''}${groupeId ? ', fusionnée' : ''}${reservationAffichee ? `, réservation ${reservationAffichee.nom} à ${heureDe(reservationAffichee.debut)}, ${reservationAffichee.couverts} couverts` : ''}. ${estSelectionnee ? 'Choisie : flèches pour déplacer, Entrée pour le menu, Échap pour annuler.' : 'Entrée pour la choisir.'}`}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !estSelectionnee) { e.preventDefault(); e.stopPropagation(); onActiver(table); } }}
        onPointerDown={(e) => onPointerDown(e, table)}
        onContextMenu={(e) => onContextMenu(e, table)}
        filter={
          estSelectionnee
            ? 'url(#lueur-selection)'
            : estMiseEnEvidence
            ? 'url(#lueur-selection)'
            : 'url(#ombre-table)'
        }
      >
        {/* Sièges dessinés autour de la table */}
        {sieges.map((s, idx) => (
          <rect
            key={idx}
            x={s.x}
            y={s.y}
            width={s.w}
            height={s.h}
            rx={3}
            transform={s.rot ? `rotate(${s.rot} ${s.x + s.w / 2} ${s.y + s.h / 2})` : undefined}
            fill={'var(--border-color)'}
            stroke={'var(--pds-trait)'}
            strokeWidth="1"
            className="pointer-events-none"
          />
        ))}

        {/* Plateau de la table selon sa forme */}
        {forme === 'ronde' ? (
          <circle
            cx={largeur / 2}
            cy={hauteur / 2}
            r={largeur / 2}
            fill={styleStatut.fillTable}
            stroke={estSelectionnee ? 'var(--pds-info-fg)' : styleStatut.strokeTable}
            strokeWidth={estSelectionnee ? 3 : 2}
          />
        ) : forme === 'ovale' ? (
          <ellipse
            cx={largeur / 2}
            cy={hauteur / 2}
            rx={largeur / 2}
            ry={hauteur / 2}
            fill={styleStatut.fillTable}
            stroke={estSelectionnee ? 'var(--pds-info-fg)' : styleStatut.strokeTable}
            strokeWidth={estSelectionnee ? 3 : 2}
          />
        ) : (
          <rect
            width={largeur}
            height={hauteur}
            rx={forme === 'plateau' ? 12 : 6}
            fill={styleStatut.fillTable}
            stroke={estSelectionnee ? 'var(--pds-info-fg)' : styleStatut.strokeTable}
            strokeWidth={estSelectionnee ? 3 : 2}
          />
        )}

        {/* Bague de fusion si la table est combinée */}
        {groupeId && (
          <rect
            x={-3}
            y={-3}
            width={largeur + 6}
            height={hauteur + 6}
            rx={8}
            fill="none"
            stroke="var(--pds-info-fg)"
            strokeWidth="1.5"
            strokeDasharray="4 2"
            className="pointer-events-none"
          />
        )}

        {/* Halo pulsant si table "à libérer" */}
        {styleStatut.pulsant && (
          <circle
            cx={largeur / 2}
            cy={hauteur / 2}
            r={Math.min(largeur, hauteur) / 2 + 8}
            fill="none"
            stroke="var(--pds-warn-fg)"
            strokeWidth="2"
            strokeDasharray="4 4"
            className="animate-spin pointer-events-none"
            style={{ animationDuration: '6s' }}
          />
        )}

        {/* Contenu central de la table : Nom + Capacité */}
        <g className="pointer-events-none select-none">
          {/* Nom de la table */}
          <text
            x={largeur / 2}
            y={texteReservation ? hauteur / 2 - 13 : hauteur / 2 - 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="14"
            fontWeight="bold"
            fill={'var(--text-primary)'}
          >
            {nom}
          </text>

          {/* Capacité (ex: 4 pl.) */}
          <text
            x={largeur / 2}
            y={texteReservation ? hauteur / 2 + 1 : hauteur / 2 + 13}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="10"
            fontWeight="500"
            fill={'var(--text-muted)'}
          >
            {capaciteMax} pl.
          </text>

          {/* Réservation en cours, ou la prochaine : heure et nom */}
          {texteReservation && (
            <text
              x={largeur / 2}
              y={hauteur / 2 + 14}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="9"
              fontWeight="600"
              fill={'var(--text-secondary)'}
            >
              {texteReservation}
            </text>
          )}
        </g>

        {/* Badge d'état : Jamais la couleur seule (forme géométrique + libellé court) */}
        <g
          transform={`translate(${largeur / 2}, ${hauteur - 6})`}
          className="pointer-events-none select-none"
        >
          {/* Badge forme géométrique */}
          <rect
            x="-26"
            y="-7"
            width="52"
            height="14"
            rx="3"
            fill={styleStatut.badgeBg}
            stroke={'var(--bg-card)'}
            strokeWidth="1"
          />
          <text
            x="0"
            y="0"
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="8"
            fontWeight="bold"
            fill={styleStatut.badgeTexte}
            letterSpacing="0.4"
          >
            {styleStatut.libelleCourt}
          </text>
        </g>

        {/* Chronomètre ou icône d'alerte dans le coin supérieur droit */}
        {statut === 'en_cours' && statutInfo.minutesEcoulees !== undefined && (
          <g
            transform={`translate(${largeur - 10}, 8)`}
            className="pointer-events-none select-none"
          >
            <circle cx="0" cy="0" r="8" fill="var(--pds-info-fg)" />
            <text
              x="0"
              y="1"
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="8"
              fontWeight="bold"
              fill="#ffffff"
            >
              {statutInfo.minutesEcoulees}m
            </text>
          </g>
        )}

        {statut === 'a_liberer' && (
          <g
            transform={`translate(${largeur - 10}, 8)`}
            className="pointer-events-none select-none"
          >
            <polygon points="0,-8 7,6 -7,6" fill="var(--pds-warn-fg)" />
            <text
              x="0"
              y="2"
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="8"
              fontWeight="bold"
              fill="#ffffff"
            >
              !
            </text>
          </g>
        )}

        {bloquee && (
          <g
            transform={`translate(${largeur - 10}, 8)`}
            className="pointer-events-none select-none"
          >
            <circle cx="0" cy="0" r="7" fill="var(--pds-danger-fg)" />
            <path
              d="M -3,-1 L -3,3 L 3,3 L 3,-1 Z M -2,-1 A 2,2 0 0,1 2,-1"
              fill="none"
              stroke="#ffffff"
              strokeWidth="1.2"
            />
          </g>
        )}

        {/* Poignée de rotation active si table sélectionnée (sauf en lecture seule) */}
        {estSelectionnee && !lectureSeule && (
          <g
            transform={`translate(${largeur / 2}, ${-18})`}
            className="cursor-crosshair pointer-events-auto"
            onPointerDown={(e) => onDemarrerRotation(e, table)}
            aria-label="Faire pivoter la table par pas de 15 degrés"
          >
            <line
              x1="0"
              y1="4"
              x2="0"
              y2="18"
              stroke="var(--pds-info-fg)"
              strokeWidth="1.5"
              strokeDasharray="2 2"
            />
            <circle cx="0" cy="0" r="9" fill="var(--pds-info-fg)" stroke="#ffffff" strokeWidth="1.5" />
            <path
              d="M -4,-2 A 5,5 0 1,1 -2,4"
              fill="none"
              stroke="#ffffff"
              strokeWidth="1.5"
            />
          </g>
        )}
      </g>
    );
  }
);

/**
 * Ligne d'aimantation visuelle (aide au calage bord à bord).
 */
const LigneRepereAimantation: React.FC<{
  tableDeplacee: TablePlan;
  curX: number;
  curY: number;
}> = ({ tableDeplacee, curX, curY }) => {
  return (
    <g className="pointer-events-none">
      <rect
        x={curX - 2}
        y={curY - 2}
        width={tableDeplacee.largeur + 4}
        height={tableDeplacee.hauteur + 4}
        fill="none"
        stroke="var(--pds-ok-fg)"
        strokeWidth="2"
        strokeDasharray="4 4"
      />
      <circle cx={curX} cy={curY} r="4" fill="var(--pds-ok-fg)" />
      <circle
        cx={curX + tableDeplacee.largeur}
        cy={curY + tableDeplacee.hauteur}
        r="4"
        fill="var(--pds-ok-fg)"
      />
    </g>
  );
};

/**
 * Calcule les positions des petits sièges autour d'une table selon sa géométrie et capacité.
 */
interface SiegePos {
  x: number;
  y: number;
  w: number;
  h: number;
  rot?: number;
}

function calculerSiegesAutour(
  largeur: number,
  hauteur: number,
  forme: FormeTable,
  capacite: number
): SiegePos[] {
  const sieges: SiegePos[] = [];
  const tailleSiegeW = 14;
  const tailleSiegeH = 7;
  const decalage = 8; // distance par rapport au bord de la table

  if (forme === 'ronde' || forme === 'ovale') {
    const rx = largeur / 2 + decalage;
    const ry = hauteur / 2 + decalage;
    const centreX = largeur / 2;
    const centreY = hauteur / 2;

    for (let i = 0; i < capacite; i++) {
      const angle = (2 * Math.PI * i) / capacite - Math.PI / 2;
      const x = centreX + rx * Math.cos(angle) - tailleSiegeW / 2;
      const y = centreY + ry * Math.sin(angle) - tailleSiegeH / 2;
      const rot = (angle * 180) / Math.PI + 90;
      sieges.push({ x, y, w: tailleSiegeW, h: tailleSiegeH, rot });
    }
  } else {
    // Forme carrée, rectangulaire, plateau
    // Répartir les chaises sur les 4 côtés
    const cotes = {
      haut: Math.ceil(capacite / 4),
      bas: Math.floor(capacite / 4),
      gauche: Math.round((capacite - Math.ceil(capacite / 4) - Math.floor(capacite / 4)) / 2),
      droite: 0,
    };
    cotes.droite = capacite - cotes.haut - cotes.bas - cotes.gauche;

    // Côté haut
    for (let i = 0; i < cotes.haut; i++) {
      const pas = largeur / (cotes.haut + 1);
      sieges.push({
        x: pas * (i + 1) - tailleSiegeW / 2,
        y: -decalage - tailleSiegeH / 2,
        w: tailleSiegeW,
        h: tailleSiegeH,
      });
    }

    // Côté bas
    for (let i = 0; i < cotes.bas; i++) {
      const pas = largeur / (cotes.bas + 1);
      sieges.push({
        x: pas * (i + 1) - tailleSiegeW / 2,
        y: hauteur + decalage - tailleSiegeH / 2,
        w: tailleSiegeW,
        h: tailleSiegeH,
      });
    }

    // Côté gauche
    for (let i = 0; i < cotes.gauche; i++) {
      const pas = hauteur / (cotes.gauche + 1);
      sieges.push({
        x: -decalage - tailleSiegeH / 2,
        y: pas * (i + 1) - tailleSiegeW / 2,
        w: tailleSiegeH,
        h: tailleSiegeW,
      });
    }

    // Côté droit
    for (let i = 0; i < cotes.droite; i++) {
      const pas = hauteur / (cotes.droite + 1);
      sieges.push({
        x: largeur + decalage - tailleSiegeH / 2,
        y: pas * (i + 1) - tailleSiegeW / 2,
        w: tailleSiegeH,
        h: tailleSiegeW,
      });
    }
  }

  return sieges;
}
